import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type MapSearchService from 'garaje/services/map-search';

interface SearchBarArgs {
  inputAriaLabel?: string;
  inputId?: string;
  placeholder?: string;
  value?: string;
  showClearButton?: boolean;
  isLoading?: boolean;
  showResults?: boolean;
  onInput?: (event: Event) => void;
  onClear?: () => void;
  onClick?: (event: Event) => void;
  onFocus?: (event: Event) => void;
  containerClass?: string;
}

export default class SearchBar extends Component<SearchBarArgs> {
  @service declare mapSearch: MapSearchService;

  /**
   * Manages dropdown visibility state.
   * The dropdown closes when clicking outside but preserves search input and results.
   * Clicking back into the input reopens the dropdown with the same results.
   */
  @tracked isDropdownOpen = false;

  get shouldShowDropdown(): boolean {
    return this.isDropdownOpen && (this.args.showResults ?? false);
  }

  @action
  handleClick(): void {
    // Re-open dropdown when clicking on input if we have results
    if (this.args.showResults) {
      this.isDropdownOpen = true;
    }
  }

  @action
  handleFocusInput(): void {
    // Re-open dropdown when focusing on input if we have results
    if (this.args.showResults) {
      this.isDropdownOpen = true;
    }
  }

  @action
  handleInputChange(event: Event): void {
    // Open dropdown when user starts typing
    this.isDropdownOpen = true;

    // Call the parent's onInput handler
    if (this.args.onInput) {
      this.args.onInput(event);
    }
  }

  @action
  handleClear(): void {
    // Close dropdown when clearing
    this.isDropdownOpen = false;

    // Call the parent's onClear handler
    if (this.args.onClear) {
      this.args.onClear();
    }
  }

  handleClickOutside = (event: Event): void => {
    const target = event.target as Element;
    const searchBarElement = document.getElementById('search-bar');

    // Check if the click was outside the search bar
    if (searchBarElement && !searchBarElement.contains(target)) {
      this.isDropdownOpen = false;
    }
  };

  @action
  closeDropdown(): void {
    this.isDropdownOpen = false;
  }
}
